import React, {
  createContext,
  PropsWithChildren,
  useMemo,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
} from 'react';
import values from 'lodash/values';
import {
  QueryObserverResult,
  useQuery,
} from '@tanstack/react-query';
import {
  PagePropertiesDTO,
} from '@/dtos';
import {
  Pagination,
} from '@/types/common';

export type ContextProps = {
  data: any[];
  refetch: ((...params: any[]) => Promise<QueryObserverResult<any, unknown>>) | null;
  isLoading: boolean;
  isFetching: boolean;
  isFetched: boolean;
  isError: boolean;
  isEmpty: boolean;
  pagination: Pagination;
  setPagination: Dispatch<SetStateAction<Pagination>>;
  totalSize: number;
  setTotalSize: Dispatch<SetStateAction<number>>;
};
export const DataContext = createContext<Partial<ContextProps>>({});

type Props<K, T> = PropsWithChildren & {
  normalize?: (raw: K) => T[] | undefined;
  getData: (...params: any[]) => Promise<K> | K;
  getQuery: (...params: any[]) => object;
  getMeta?: (data: K) => PagePropertiesDTO;
  params?: Record<string, any>;
  enabled?: boolean;
  pagination?: Partial<Pagination>;
};

export default function DataProvider<K, T>({
  params,
  getData,
  getQuery,
  getMeta,
  normalize,
  enabled = true,
  pagination,
  children,
}: Props<K, T>) {
  const [totalSize, setTotalSize] = useState<number>(0);
  const [dataPagination, setPagination] = useState<Pagination>({
    pageSize: pagination?.pageSize ?? 25,
    pageIndex: pagination?.pageIndex ?? 0,
    page: pagination?.page ?? 0,
  });

  const queryParams = useMemo(() => ({
    ...params,
    pagination: dataPagination,
  }), [params, dataPagination]);

  const {
    data,
    isFetching,
    isLoading,
    isError,
    isPending,
    isFetched,
    refetch,
  } = useQuery<K, Error>({
    queryKey: [...values(queryParams)],
    queryFn: () => getData(getQuery(queryParams)),
    enabled,
  });

  useEffect(() => {
    const meta = getMeta?.((data ?? {}) as K) ?? {};
    setTotalSize(meta?.totalSize ?? 0);
  }, [data]);

  const normalized = useMemo(() => (
    normalize?.((data ?? {}) as K) ?? data as T[]
  ), [data, queryParams]);

  const value = useMemo(() => ({
    data: normalized,
    refetch,
    isLoading,
    isFetching,
    isFetched,
    isError,
    isEmpty: normalized?.length === 0 && isFetched,
    setPagination,
    pagination: dataPagination,
    totalSize,
    setTotalSize,
  }), [
    data,
    isLoading,
    isFetching,
    isFetched,
    isError,
    isFetched,
    dataPagination,
    setPagination,
    totalSize,
    setTotalSize,
  ]);

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
}
